import {
  isLaunchpadSupported,
  launchpadDefinitions,
  socialLaunchpads,
} from '@/constants/launchpads';

import { fontStyles } from '@/styles/typography';

import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { classNames } from '@/utils/classnames';
import { makeImageProxyUrl, tokenImageBackgroundColor } from '@/utils/search';

import { Avatar, AvatarProps } from '../Avatar';
import { getAvatarBadgeStyles } from '../Avatar/utils';
import { ChainAvatar } from '../ChainAvatar';
import { LaunchpadAvatar } from '../LaunchpadAvatar';

type TokenAvatarProps = AvatarProps & {
  /**
   * Address of the token.
   */
  address?: string;
  /**
   * Chain ID of the token.
   */
  chainId?: number;
  /**
   * ID of the launchpad used to launch the token.
   */
  launchpad?: string | null;
  /**
   * Whether to show the chain logo.
   * @default true
   */
  showChainLogo?: boolean;
};

export const TokenAvatar = (props: TokenAvatarProps) => {
  const {
    chainId,
    address,
    launchpad,
    showChainLogo = true,
    size = 32,
    className,
    ...otherProps
  } = props;

  const tokenLogoSrc =
    chainId != null && address != null ? makeImageProxyUrl(chainId, address) : undefined;
  const backgroundColor =
    chainId != null && address != null ? tokenImageBackgroundColor(chainId, address) : undefined;

  const chainBadgeStyles = getAvatarBadgeStyles(size, 'bottom-right');
  const launchpadBadgeStyles = getAvatarBadgeStyles(size, 'top-right');

  const isSocialLaunchpad =
    launchpad != null && (socialLaunchpads as readonly string[]).includes(launchpad);
  const { name: launchpadName } = isLaunchpadSupported(launchpad)
    ? launchpadDefinitions[launchpad]
    : {};

  return (
    <div
      className={classNames(
        'relative inline-flex drop-shadow-[var(--token-shadow-15)] shrink-0',
        className,
      )}
    >
      <div className="flex" style={showChainLogo ? chainBadgeStyles.maskStyle : undefined}>
        <div className="flex" style={launchpad ? launchpadBadgeStyles.maskStyle : undefined}>
          <Avatar
            size={size}
            src={tokenLogoSrc}
            fallbackSrc="/images/chains/none.svg"
            className="shadow-none!"
            {...otherProps}
            style={{ backgroundColor, ...otherProps.style }}
          />
        </div>
      </div>
      {showChainLogo && <ChainAvatar chainId={chainId} {...chainBadgeStyles.badgeProps} />}
      {launchpad && <LaunchpadAvatar id={launchpad} {...launchpadBadgeStyles.badgeProps} />}
      {launchpad && (
        <MatchaTooltip>
          <MatchaTooltipTrigger>
            <div className="absolute size-3" style={launchpadBadgeStyles.badgeProps.style} />
          </MatchaTooltipTrigger>
          <MatchaTooltipContent>
            <div className="flex flex-row gap-6 items-center">
              <p className={fontStyles({ variant: 'BODY_SM', class: 'text-token-gray-900' })}>
                {isSocialLaunchpad ? 'Type' : 'Launchpad'}
              </p>
              <div className="flex flex-row gap-1 items-center">
                <LaunchpadAvatar id={launchpad} alt={`${launchpad} logo`} size={16} />
                <p className={fontStyles({ variant: 'BODY_MD' })}>{launchpadName ?? launchpad}</p>
              </div>
            </div>
          </MatchaTooltipContent>
          <MatchaTooltipPopover sideOffset={8} className="py-3" />
        </MatchaTooltip>
      )}
    </div>
  );
};
